/**
 * 腾讯云COS前端直传工具
 * 使用临时密钥直接上传文件到COS，不经过后端服务器中转
 */
import COS from 'cos-js-sdk-v5'
import request from '@/utils/request'
import { cdnUrl } from '@/constant/cdn'
import { computeCompressSize } from '@/utils/imageResize'
import { VIDEO_UPLOAD_DIRECTORY } from '@/utils/articleMedia'

// COS 实例（单例）
let cosInstance = null

/**
 * 获取COS临时上传凭证
 * @param {string} directory - 上传目录前缀，如 'userPic'、'article'、'avatar'
 * @returns {Promise<Object>} 凭证信息
 */
export async function getCosCredential(directory = 'upload') {
  const res = await request({
    url: '/v1/cos/credential',
    method: 'get',
    params: { directory }
  })
  if (res.code !== 200 || !res.data) {
    throw new Error(res.message || '获取上传凭证失败')
  }
  return res.data
}

/**
 * 使用已有凭证创建COS实例，避免重复请求凭证接口
 * @param {Object} credential - 已获取的凭证信息
 * @returns {COS} COS实例
 */
function getCosInstanceWithCredential(credential) {
  return new COS({
    getAuthorization: function (options, callback) {
      callback({
        TmpSecretId: credential.tmpSecretId,
        TmpSecretKey: credential.tmpSecretKey,
        SecurityToken: credential.sessionToken,
        StartTime: credential.startTime,
        ExpiredTime: credential.expiredTime
      })
    }
  })
}

/**
 * 生成唯一文件名
 * @param {string} keyPrefix - 文件key前缀（如 'userPic/20260427/'）
 * @param {string} originalName - 原始文件名
 * @returns {string} 完整的文件key
 */
function generateFileName(keyPrefix, originalName) {
  const suffix = originalName.substring(originalName.lastIndexOf('.'))
  const timestamp = Date.now()
  const random = Math.random().toString(36).substr(2, 8)
  return `${keyPrefix}${timestamp}_${random}${suffix}`
}

/**
 * 上传文件到腾讯云COS（核心方法）
 * @param {File} file - 要上传的文件对象
 * @param {Object} options - 上传选项
 * @param {string} [options.directory='upload'] - 上传目录前缀
 * @param {Function} [options.onProgress] - 上传进度回调 (percent: number) => void
 * @returns {Promise<string>} 文件访问URL
 */
export async function uploadToCos(file, options = {}) {
  const { directory = 'upload', onProgress } = options

  // 1. 获取临时凭证（包含 bucket、region、keyPrefix）
  const credential = await getCosCredential(directory)
  const { bucket, region, keyPrefix } = credential

  // 2. 创建COS实例（复用已获取的凭证，避免重复请求）
  const cos = getCosInstanceWithCredential(credential)

  // 3. 生成文件名
  const fileName = generateFileName(keyPrefix, file.name)

  // 4. 执行上传
  return new Promise((resolve, reject) => {
    // 大于5MB使用分片上传，否则使用简单上传
    const uploadMethod = file.size > 5 * 1024 * 1024 ? 'sliceUploadFile' : 'putObject'

    cos[uploadMethod]({
      Bucket: bucket,
      Region: region,
      Key: fileName,
      Body: file,
      onProgress: function (progressData) {
        if (onProgress) {
          onProgress(Math.round(progressData.percent * 100))
        }
      }
    }, function (err, data) {
      if (err) {
        console.error('COS上传失败:', err)
        reject(new Error(err.message || '文件上传失败'))
      } else {
        // 拼接文件访问URL（使用CDN域名）。
        // 域名统一在 @/constant/cdn，别在这里写字面量：这个地址是会落库的，
        // 换域名时漏改一处，之后由这条路径上传的图片就永久指向打不开的地址。
        const url = cdnUrl(fileName)
        resolve(url)
      }
    })
  })
}

/**
 * 上传视频到腾讯云COS（发视频帖用）
 *
 * 和 uploadToCos 的区别：
 * 1. 目录固定为 article/video：服务端发凭证时会把 keyPrefix 强制成 article/video/{登录账号id}/{yyyyMMdd}/，
 *    发帖时再校验视频确实在当前账号自己的目录下（ArticleVideoSupport.requireOwnVideoKey）；
 * 2. 文件名只用「时间戳_随机串.扩展名」，扩展名只能是 mp4 / mov / m4v ——
 *    服务端按 ^\d{8}/[A-Za-z0-9_-]{1,80}\.(mp4|mov|m4v)$ 校验，原文件名里的中文、空格都不能带上去；
 * 3. 显式写 Content-Type：发帖时服务端 HEAD 对象，只认 video/* 和 octet-stream；
 * 4. 可以取消（signal），视频动辄几十 MB，上传中用户要能反悔。
 *
 * 一律用简单上传（PutObject，一个请求传完），和 App 端的表单直传口径一致：
 * 凭证对简单上传限制了 200MB 上限，正好是视频大小上限；分片上传还依赖存储桶 CORS 暴露 ETag，
 * 少配一项就整体失败，单个视频最大 200MB，一个请求传完更稳。
 *
 * @param {File} file 视频文件
 * @param {Object} [options]
 * @param {String} options.ext 扩展名（不带点），调用方已按 videoExtOf 校验过
 * @param {String} options.contentType MIME
 * @param {Function} [options.onProgress] 进度回调 ({ percent, loaded, total }) => void，percent 为 0~100
 * @param {AbortSignal} [options.signal] 取消上传；取消后 Promise 以 error.aborted === true 的错误 reject
 * @returns {Promise<string>} 视频的 CDN 地址
 */
export async function uploadVideoToCos (file, options = {}) {
  const { ext, contentType, onProgress, signal } = options
  const abortedError = () => {
    const error = new Error('已取消上传')
    error.aborted = true
    return error
  }
  if (signal && signal.aborted) throw abortedError()

  const credential = await getCosCredential(VIDEO_UPLOAD_DIRECTORY)
  if (signal && signal.aborted) throw abortedError()

  const { bucket, region, keyPrefix } = credential
  const suffix = String(ext || 'mp4').replace(/^\./, '').toLowerCase()
  const random = Math.random().toString(36).substr(2, 8)
  const fileKey = `${keyPrefix}${Date.now()}_${random}.${suffix}`
  const cos = getCosInstanceWithCredential(credential)

  return new Promise((resolve, reject) => {
    let taskId = ''
    let settled = false
    const settle = (fn, value) => {
      if (settled) return
      settled = true
      if (signal) signal.removeEventListener('abort', onAbort)
      fn(value)
    }
    // 取消时立刻按取消结算，再去停底层任务：COS SDK 取消任务后不会再回调，不自己结算 Promise 会一直挂着
    const onAbort = () => {
      if (taskId) {
        try {
          cos.cancelTask(taskId)
        } catch (e) {
          // 任务已经结束
        }
      }
      settle(reject, abortedError())
    }
    if (signal) signal.addEventListener('abort', onAbort)

    cos.putObject({
      Bucket: bucket,
      Region: region,
      Key: fileKey,
      Body: file,
      ContentType: contentType || file.type || 'video/mp4',
      onTaskReady (id) {
        taskId = id
      },
      onProgress (progressData) {
        if (settled || !onProgress) return
        onProgress({
          percent: Math.round((Number(progressData.percent) || 0) * 100),
          loaded: Number(progressData.loaded) || 0,
          total: Number(progressData.total) || file.size || 0
        })
      }
    }, (err) => {
      if (err) {
        console.error('COS视频上传失败:', err)
        const message = err.statusCode === 403
          ? '视频上传被拒绝，请确认视频不超过200MB'
          : (err.message || '视频上传失败，请重试')
        settle(reject, new Error(message))
        return
      }
      settle(resolve, cdnUrl(fileKey))
    })
  })
}

/**
 * 上传图片到COS（带压缩处理）
 * 封装了图片校验、压缩、上传的完整流程
 * @param {File} file - 图片文件
 * @param {Object} options - 上传选项
 * @param {string} [options.directory='userPic'] - 上传目录
 * @param {number} [options.maxSize=5] - 最大文件大小(MB)
 * @param {Function} [options.onProgress] - 进度回调
 * @param {boolean} [options.compress=true] - 是否压缩
 * @returns {Promise<string>} 图片访问URL
 */
export async function uploadImageToCos(file, options = {}) {
  const {
    directory = 'userPic',
    maxSize = 5,
    onProgress,
    compress = true
  } = options

  // 1. 校验文件类型
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  if (!validTypes.includes(file.type.toLowerCase())) {
    throw new Error('只支持 JPG、PNG、GIF、WebP 格式的图片!')
  }

  // 2. 校验文件大小
  if (file.size / 1024 / 1024 > maxSize) {
    throw new Error(`图片大小不能超过${maxSize}MB!`)
  }

  // 3. 压缩图片
  let processedFile = file
  if (compress) {
    processedFile = await compressImage(file)
  }

  // 4. 上传到COS
  return uploadToCos(processedFile, { directory, onProgress })
}

// 小于该体积、且尺寸不用缩的图片原样上传，避免对本来就不大的图做无意义的二次编码掉画质
const SKIP_COMPRESS_SIZE = 500 * 1024
// JPEG 压缩质量，与 App 端（utils/imageHelper.js 的 80）一致。
// 原来按体积降到 0.6~0.7，大图恰恰多是长截图，文字边缘会出现明显的块状噪点
const DEFAULT_QUALITY = 0.8

/**
 * 压缩图片（上传前）
 *
 * 尺寸规则见 @/utils/imageResize：普通图（长边:短边 ≤ 2）长边不超过 maxDimension；
 * 长图改为限制短边（≤1280），长边等比保留。原来统一按最长边 1920 缩，
 * 长图宽度会被压成细条（1080x3626 → 572x1920），这是长图发帖后变糊的原因。
 *
 * @param {File} file - 原始图片文件
 * @param {Object} [options] - 压缩选项
 * @param {number} [options.maxDimension=1920] - 普通图的长边上限
 * @param {number} [options.quality=0.8] - JPEG 质量（0~1）
 * @returns {Promise<File>} 压缩后的图片文件（不需要压缩时返回原文件）
 */
export function compressImage(file, options = {}) {
  const { maxDimension = 1920, quality = DEFAULT_QUALITY } = options

  return new Promise((resolve, reject) => {
    // 用 object URL 而不是 FileReader 读成 base64：大图（长截图动辄几 MB）
    // 转成 base64 字符串要多占 1/3 的内存，手机浏览器上容易被拖垮
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      try {
        const target = computeCompressSize(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDimension)
        // 读不到尺寸（理论上不会发生）就原样上传
        if (!target) {
          resolve(file)
          return
        }

        // 尺寸不用缩、文件也不大，直接返回原文件
        if (!target.resized && file.size < SKIP_COMPRESS_SIZE) {
          resolve(file)
          return
        }

        const { width, height } = target
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('图片压缩失败'))
          return
        }
        // JPEG 没有透明通道，先铺白底，免得透明 PNG 变成黑底
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, width, height)
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            // 及时释放画布：iOS Safari 对页面里 canvas 的总内存有上限，连续压几张大图会超
            canvas.width = 0
            canvas.height = 0

            if (!blob) {
              reject(new Error('图片压缩失败'))
              return
            }

            const baseName = /\.\w+$/.test(file.name) ? file.name.replace(/\.\w+$/, '') : file.name
            const compressedFile = new File(
              [blob],
              `${baseName || 'image'}.jpg`,
              { type: 'image/jpeg', lastModified: Date.now() }
            )

            // 尺寸没变、只是重新编码时，结果反而更大就用原文件；
            // 缩过尺寸的必须用压缩结果，原图超出了尺寸上限
            resolve(!target.resized && compressedFile.size > file.size ? file : compressedFile)
          },
          'image/jpeg',
          quality
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('图片加载失败'))
    }
    img.src = objectUrl
  })
}

export default {
  uploadToCos,
  uploadVideoToCos,
  uploadImageToCos,
  compressImage,
  getCosCredential
}
